import { useLocation } from 'react-router-dom';
import { useEntityLabel } from '../../hooks/useEntityLabel';
import {
  buildSafeCandidateLabel,
  isSafeCandidateLabel,
  toEntityType,
} from './noteCandidateLabel';
import type { NoteEntityCandidate } from '../../types';

interface NoteCandidateOptionProps {
  readonly candidate: NoteEntityCandidate;
  /** Index into `action.candidates`; used as the <option> value so the raw
   *  entity id never reaches the DOM (OMNI-634). */
  readonly value: string;
}

/**
 * One entry in the Note Draft "Save to" selector.
 *
 * Its own component (rather than an inline <option>) so each candidate can run
 * its own `useEntityLabel` lookup when the server label would otherwise leak an
 * internal id. The lookup is React-Query cached and shares its key with the
 * context chip, so the entity on the current screen is usually already resolved.
 */
export default function NoteCandidateOption({
  candidate,
  value,
}: NoteCandidateOptionProps) {
  const { pathname } = useLocation();

  // Passing undefined keeps `useEntityLabel`'s query disabled, so a label that
  // is already human-readable costs no request.
  const needsLookup = !isSafeCandidateLabel(candidate.label);
  const { data: resolved } = useEntityLabel(
    needsLookup ? toEntityType(candidate.entityType) : undefined,
    needsLookup ? candidate.entityId : undefined,
    pathname
  );

  return (
    <option value={value}>
      {buildSafeCandidateLabel(candidate, resolved)}
    </option>
  );
}
